import { ComponentParams, ComponentRendering, Field } from "@sitecore-jss/sitecore-jss-nextjs";
import {
    Heading,
    Icon,
    IconButton,
    Link,
    List,
    ListItem,
    Text,
    Box,
    Stack,
    Skeleton,
} from "@chakra-ui/react";
import { FormEvent, useEffect, useState } from "react";
import { useRouter } from "next/router";
import ArrowForwardIcon from "@material-design-icons/svg/filled/arrow_forward_ios.svg";
import NextLink from "next/link";
import CrossIcon from "@material-design-icons/svg/outlined/close.svg";
import ArrowRight from "@material-design-icons/svg/filled/arrow_forward.svg";
import SearchInput from "./SearchInput";
import { useI18n } from "next-localization";
import GenericHeader, { GenericHeaderProps } from "components/ui/GenericHeader";

interface SearchResult {
    title: string;
    url: string;
    content: string;
    breadcrumb?: string;
    id: string;
}

interface PaginationPage {
    Page: number;
    Label: string;
    Href: string;
    Offset: number;
}

interface SearchResultData {
    SearchId: number;
    Offset: number;
    Documents: {
        Id: string;
        Properties: { Name: string; Value: string; ValueMarkup: string }[];
    }[];
    TotalResults: number;
    Decorations: {
        Pagination: {
            Pages: PaginationPage[];
            CurrentPage: number;
        };
    };
}

interface Fields extends GenericHeaderProps {
    AnkiroSearchProfile: Field<string>;
}

export type SearchResultsProps = {
    rendering: ComponentRendering & { params: ComponentParams };
    params: { [key: string]: string };
    fields: Fields;
};

const extractSearchResults = (documents: SearchResultData["Documents"]): SearchResult[] => {
    const results: SearchResult[] = [];

    documents.forEach((document) => {
        const result: SearchResult = { title: "", url: "", content: "", id: "" };

        document.Properties.forEach((property) => {
            if (property.Name === "Title" && result.title === "") {
                result.title = property.Value;
            } else if (property.Name === "AltTitle" && property.Value !== "") {
                result.title = property.Value;
            } else if (property.Name === "Uri" && result.url === "") {
                result.url = property.Value;
            } else if (property.Name === "AltUri" && property.Value !== "") {
                result.url = property.Value;
            } else if (property.Name === "Content") {
                result.content = property.ValueMarkup;
            } else if (property.Name === "Breadcrumb") {
                result.breadcrumb = property.Value;
            }
        });

        result.id = document.Id;
        results.push(result);
    });

    return results;
};

const SearchResults = (props: SearchResultsProps) => {
    const { t } = useI18n();

    const AnkiroSearchProfile = "SimCorp.com";

    const { asPath, query, push } = useRouter();
    const pageUrl = asPath.split("?")[0];

    const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
    const [paginationPages, setPaginationPages] = useState<PaginationPage[]>([]);
    const [offset, setOffset] = useState(0);
    const [decorations, setDecorations] = useState({
        Pagination: {
            Pages: [],
            CurrentPage: 0,
        },
    });

    const [loading, setLoading] = useState<boolean>(false);

    const setStates = (searchData: SearchResultData) => {
        setOffset(searchData.Offset);
        setDecorations({
            Pagination: {
                Pages: [],
                CurrentPage: searchData.Decorations.Pagination.CurrentPage,
            },
        });

        if (searchData.TotalResults > 0) {
            const extractedResults = extractSearchResults(searchData.Documents);
            setSearchResults(extractedResults);

            if (searchData.Decorations.Pagination.Pages?.length > 0) {
                setPaginationPages(searchData.Decorations.Pagination.Pages);
            }
        } else {
            setSearchResults([]);
            setPaginationPages([]);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const startIndex = query.startIndex ? parseInt(query.startIndex as string) : 1;
                const termUrlQueryString =
                    "?q=" +
                    (query.q ? encodeURIComponent(String(query.q)) : "") +
                    (startIndex > 1 ? "&startIndex=" + startIndex : "");
                const termUrl = `https://simcorp.ankiro.dk/Rest/${AnkiroSearchProfile}/Search/${termUrlQueryString}`;

                const response = await fetch(termUrl);
                if (!response.ok) {
                    throw new Error("Network response was not ok");
                }

                const searchData: SearchResultData = await response.json();

                setStates(searchData);
            } catch (error) {
                console.error("Fetch Error :-S", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [query]);

    const createSearchTermListItem = (result: SearchResult) => {
        return (
            <ListItem key={result.id}>
                <Link as={NextLink} href={result.url}>
                    <Stack spacing={{ base: "sp8", lg: "sp16" }}>
                        <Skeleton
                            isLoaded={!loading}
                            startColor="brand.lightGray"
                            fitContent={true}
                            borderRadius={"base"}
                        >
                            <Heading
                                as="h3"
                                size="sm"
                                textDecoration={"underline"}
                                lineHeight={"base"}
                            >
                                {result.title}
                            </Heading>
                        </Skeleton>
                        <Skeleton
                            isLoaded={!loading}
                            startColor="brand.lightGray"
                            fitContent={true}
                            borderRadius={"base"}
                        >
                            <Text
                                lineHeight={"tall"}
                                sx={{
                                    ".max": {
                                        fontWeight: "bold",
                                    },
                                }}
                                dangerouslySetInnerHTML={{ __html: result.content }}
                            />
                        </Skeleton>
                        {result.breadcrumb && (
                            <Skeleton
                                isLoaded={!loading}
                                startColor="brand.lightGray"
                                fitContent={true}
                                borderRadius={"base"}
                            >
                                <Text lineHeight={"base"} size="xs">
                                    {result.breadcrumb.split(" — ").map((part, index, array) => {
                                        const isLastPart = index === array.length - 1;
                                        return (
                                            <Text
                                                as="span"
                                                fontWeight={isLastPart ? "bold" : ""}
                                                opacity={isLastPart ? "1" : "0.5"}
                                                key={result.id}
                                            >
                                                {part}
                                                {isLastPart ? "" : " — "}
                                            </Text>
                                        );
                                    })}
                                </Text>
                            </Skeleton>
                        )}
                    </Stack>
                </Link>
            </ListItem>
        );
    };

    const createPagination = (page: PaginationPage) => {
        if (searchResults.length === 0) {
            return null;
        }
        const isActive = page.Page === decorations.Pagination.CurrentPage;
        const prevIsDisabled = offset === 0;
        const nextIsDisabled = page.Offset === offset;

        if (page.Label === "«") {
            return (
                <ListItem
                    key={`${page.Page}_prev`}
                    display={"flex"}
                    alignItems={"center"}
                    aria-hidden={prevIsDisabled}
                >
                    <Link
                        as={NextLink}
                        aria-label={!prevIsDisabled ? `${t("previousPage")}` : undefined}
                        href={`${pageUrl}${page.Href}`}
                        tabIndex={prevIsDisabled ? -1 : 0}
                        {...(prevIsDisabled && { sx: { pointerEvents: "none", opacity: 0.5 } })}
                    >
                        <Icon as={ArrowForwardIcon} sx={{ transform: "scaleX(-1)" }} />
                    </Link>
                </ListItem>
            );
        } else if (page.Label === "»") {
            return (
                <ListItem key={page.Page}>
                    <Link
                        as={NextLink}
                        href={`${pageUrl}${page.Href}`}
                        aria-label={t("nextPage")}
                        {...(nextIsDisabled && { sx: { pointerEvents: "none", opacity: 0.5 } })}
                    >
                        <Icon as={ArrowForwardIcon} />
                    </Link>
                </ListItem>
            );
        } else {
            return (
                <ListItem key={`${page.Page}_next`}>
                    <Link
                        as={NextLink}
                        href={`${pageUrl}${page.Href}`}
                        sx={{
                            textDecoration: "none",
                            _hover: {
                                textDecoration: "underline",
                            },
                            fontWeight: isActive && "bold",
                        }}
                        aria-label={
                            isActive
                                ? `${t("currentPage")} ${page.Label}`
                                : `${t("pageLinkLabel")} ${page.Label}`
                        }
                    >
                        {page.Label}
                    </Link>
                </ListItem>
            );
        }
    };

    async function onSubmit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        event.stopPropagation();
        const formData = new FormData(event.currentTarget);
        push({ pathname: pageUrl, query: { q: formData.get("q") as string } });
    }

    const clearSearch = () => {
        push({ pathname: pageUrl, query: { q: "" } });
    };

    return (
        <GenericHeader
            Id={props.rendering.uid}
            HeaderHeadline={props.fields?.HeaderHeadline}
            HeaderSubHeadline={props.fields?.HeaderSubHeadline}
            HeaderCTA={props.fields?.HeaderCTA}
            headlineSize={props.params?.HeaderHeadlineSize}
        >
            <Box position="relative">
                <form onSubmit={onSubmit}>
                    <SearchInput
                        typeAheadUrl={`https://simcorp.ankiro.dk/Rest/${AnkiroSearchProfile}/TypeAhead.json?q=`}
                    />
                    <Box position="absolute" height="full" top="0" right="0" display="flex">
                        <IconButton
                            variant={"outline"}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                clearSearch();
                            }}
                            size="lg"
                            zIndex="1"
                            overflow="hidden"
                            borderRadius={0}
                            p={{ base: "sp24", lg: "sp20" }}
                            w={"66px"}
                            height="full"
                            sx={{
                                svg: {
                                    height: "full",
                                    width: "full",
                                },
                            }}
                            aspectRatio={"1/1"}
                            borderLeft={0}
                            _hover={{
                                bg: "black",
                                svg: {
                                    fill: "white",
                                },
                            }}
                            _focus={{
                                bg: "black",
                                svg: {
                                    fill: "white",
                                },
                            }}
                            icon={<CrossIcon />}
                            aria-label={t("resetButtonLabel")}
                        />
                        <IconButton
                            variant={"primary"}
                            size="lg"
                            zIndex="1"
                            borderTopLeftRadius={0}
                            borderBottomLeftRadius={0}
                            border={"1px solid black"}
                            borderLeft="0"
                            aspectRatio={"1/1"}
                            p={{ base: "sp24", lg: "sp20" }}
                            w={"66px"}
                            height="full"
                            sx={{
                                svg: {
                                    height: "full",

                                    width: "full",
                                },
                            }}
                            _hover={{
                                bg: "white",

                                svg: {
                                    fill: "black",
                                },
                            }}
                            _focus={{
                                bg: "white",
                                border: "2px solid black",
                                svg: {
                                    fill: "black",
                                },
                            }}
                            icon={<ArrowRight />}
                            type="submit"
                            aria-label={t("searchPlaceholder")}
                        />
                    </Box>
                </form>
            </Box>

            {searchResults.length === 0 && query.q && !loading ? (
                <Box mt={{ base: "sp40", lg: "sp88" }}>
                    <Text as="p" size="bodyXl">
                        {t("noSearchResults")}
                    </Text>
                </Box>
            ) : (
                <List
                    display={"flex"}
                    mt={{ base: "sp40", lg: "sp88" }}
                    flexDir={"column"}
                    gap={"sp40"}
                >
                    {searchResults.map((result) => createSearchTermListItem(result))}
                </List>
            )}

            <nav role="navigation" aria-label={t("paginationLabel")}>
                <List
                    display={"flex"}
                    justifyContent={"center"}
                    alignItems={"center"}
                    gap={"sp20"}
                    mt={["sp24", null, null, "sp64"]}
                >
                    {paginationPages.map((page) => createPagination(page))}
                </List>
            </nav>
        </GenericHeader>
    );
};

export default SearchResults;
